import type { HTMLAttributes, ReactNode } from "react";
import { CheckboxBase } from "@/components/base/checkbox/checkbox";
import { cx } from "@/utils/cx";

export const TableSelectionCellRowState = {
	Unchecked: "unchecked",
	Checked: "checked",
	Indeterminate: "indeterminate",
} as const;

export type TableSelectionCellRowState =
	(typeof TableSelectionCellRowState)[keyof typeof TableSelectionCellRowState];

export interface TableSelectionCellRowProps
	extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
	/**
	 * Row selection checkbox state (Figma `state`).
	 * `indeterminate` when some — but not all — nested rows are selected.
	 * Ignored when `children` provides a live React Aria checkbox.
	 */
	state?: TableSelectionCellRowState;
	/**
	 * Compose a live checkbox (e.g. React Aria `Checkbox slot="selection"`).
	 * Defaults to a presentational {@link CheckboxBase} for Storybook.
	 */
	children?: ReactNode;
}

/**
 * Selection body chrome matching Figma Table/SelectionCellRow (3045:146).
 * Anchors the leading column of a table body row.
 */
export function TableSelectionCellRow({
	state = TableSelectionCellRowState.Unchecked,
	className,
	children,
	...props
}: TableSelectionCellRowProps) {
	const isIndeterminate = state === TableSelectionCellRowState.Indeterminate;
	const isSelected =
		state === TableSelectionCellRowState.Checked || isIndeterminate;

	return (
		<div
			{...props}
			data-state={state}
			className={cx(
				// Figma Table/SelectionCellRow — pl-32 pr-4; h-50 to match BodyRow
				// (py-0 + items-center — py was growing the row with BodyCell).
				"relative flex h-[50px] max-h-[50px] w-[52px] items-center overflow-hidden py-0 pr-1 pl-8",
				className,
			)}
			data-slot="table-selection-cell-row"
		>
			{children ?? (
				<CheckboxBase
					size="sm"
					isSelected={isSelected}
					isIndeterminate={isIndeterminate}
				/>
			)}
		</div>
	);
}
